import { Icons, Icon, RaisedButton, Card, Dialog, useSnackbar } from '@eduact/ed-components'
import Spacer from '@eduact/ed-components/Spacer'
import { SnackbarConfig } from '@Utilities/SnackbarConfig'
import { Typography, FlexLayout } from '@eduact/ed-system'
import { useSubmitAssignmentMutation } from '@Features/Unit/UnitApi'
import { yupResolver } from '@hookform/resolvers/yup'
import React, { Dispatch, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import styled from 'styled-components'
import { number, object, string } from 'yup'
import AttachmentAnswer from './AttachmentAnswer'
import AnswerButton from './AnswerButton'
import RichTextAnswer from '@Components/RichTextAnswer/RichTextAnswer'
import StudentAnswerHtml from '@Components/RichTextAnswer/StudentAnswerHtml'
import moment from 'moment'
import { MdInfoOutline } from 'react-icons/md'
import { useTranslation } from 'react-i18next'
import { useCourseContext } from '@Features/Course/Providers/CourseProvider/CourseProvider'
import stampAutoDir from '@Utilities/StampAutoDir'

type Props = {
	assignment: AssignmentUnit
	submission: AssignmentSubmission | null
	isEdit?: boolean
	setIsEdit?: React.Dispatch<boolean>
	canSubmit: boolean
	onChooseType: Dispatch<boolean>
}

const schema = object({
	assignment_id: number(),
	content: string().required(),
	content_type: string().required(),
	// What the client produced for a text answer (`html` from the rich editor,
	// `plain` from the textarea fallback); the API normalises both to HTML.
	content_format: string().oneOf(['html', 'plain']).notRequired(),
})
const AssignmentAnswer: React.VoidFunctionComponent<Props> = ({
	assignment,
	submission,
	isEdit = true,
	setIsEdit,
	canSubmit,
	onChooseType,
}) => {
	const [submitAssignment] = useSubmitAssignmentMutation()
	const { t } = useTranslation('assignment')
	// '*' lets the student pick; anything else is the only allowed form. The
	// backend rejects a mismatched content_type.
	const lockedAnswerType: AssignmentContentType | undefined =
		assignment.answer_schema && assignment.answer_schema !== '*' ? assignment.answer_schema : undefined

	const { course, section } = useCourseContext()
	const { setValue, watch, control, formState, handleSubmit, reset, register } = useForm<SubmitAssignmentPayload>({
		resolver: yupResolver(schema),
		mode: 'all',
		values: {
			assignment_id: Number(assignment.id),
			content: submission?.content ?? '',
			content_type: submission?.content_type,
			// A served text answer is always HTML: an untouched resubmit must
			// declare it, or the API would escape the markup as plain text.
			content_format: submission?.content_type === 'text' ? 'html' : undefined,
			course_id: course?.id.toString(),
			section_id: section?.id.toString(),
		},
	})

	useEffect(() => {
		// A locked answer form skips the chooser entirely (edit mode itself is
		// owned by the parent, derived from the submission).
		setValue('content_type', submission?.content_type ?? lockedAnswerType)
		setValue('content_format', submission?.content_type === 'text' ? 'html' : undefined)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [submission, lockedAnswerType])

	const handleChooseType = (type: AssignmentContentType) => {
		setValue('content_type', type, {
			shouldDirty: true,
			shouldTouch: true,
			shouldValidate: true,
		})
		onChooseType(true)
	}
	const toggleAnswerType = () => {
		// setValue('content', '')
		setSwitchModalOpen(true)
		// handleChooseType(answerType === 'attachment' ? 'text' : 'attachment')
	}

	const answerType = watch('content_type')

	const { isValid, isDirty, isSubmitting } = formState

	const { openSnackbar } = useSnackbar()
	const handleSubmitAssignment = handleSubmit(async values => {
		try {
			// `.unwrap()` is load-bearing: an RTK mutation never rejects on its
			// own, so a 403 (graded/locked, deadline) or a network failure used
			// to flip the UI to "submitted" anyway.
			const echo: any = await submitAssignment(values).unwrap()
			setIsEdit?.(false)
			// Keep the form on the STORED value (the API normalises the answer to
			// HTML), so a fallback-typed plain answer is not rendered as markup.
			const stored = echo?.data?.content
			reset(
				{ ...values, content: typeof stored === 'string' ? stored : values.content, content_format: values.content_type === 'text' ? 'html' : undefined },
				{ keepDirty: false }
			)
		} catch (error: any) {
			const message = error?.data?.message ?? error?.message
			openSnackbar({ ...SnackbarConfig.disclaimer, text: typeof message === 'string' && message ? message : t('submit_failed') })
		}
	})

	const hasStatus = !!submission?.status

	const [switchModalOpen, setSwitchModalOpen] = useState(false)

	const handleCloseSwitchModal = () => {
		setSwitchModalOpen(false)
	}
	const changeType = () => {
		setSwitchModalOpen(false)
		setValue('content', '')
		setValue('content_format', undefined)
		setValue('content_type', answerType === 'text' ? 'attachment' : 'text')
	}
	return (
		<>
			{(canSubmit || hasStatus) && (
				<div>
					<Dialog maxWidth={'90vw'} width={'fit-content'} height={'fit-content'} onClose={handleCloseSwitchModal} open={switchModalOpen}>
						<FlexLayout p={'2rem'} flexDirection={'column'}>
							<FlexLayout p={'2rem'} flexDirection={'column'}>
								<Typography>Are you sure to switch answer type ?</Typography>
								<Typography>NB: Answer will be erased</Typography>
							</FlexLayout>
							<FlexLayout
								gridGap={{ sm: '0.5rem', lg: '2rem' }}
								flexWrap={{ sm: 'wrap' }}
								justifyContent={{ sm: 'flex-end', lg: 'initial' }}
							>
								<RaisedButton onClick={changeType} btnSize="small">
									Switch
								</RaisedButton>
								<RaisedButton onClick={handleCloseSwitchModal} variant="princetonOrange" btnSize="small" outlined>
									Cancel
								</RaisedButton>
							</FlexLayout>
						</FlexLayout>
					</Dialog>
					{!answerType && !submission && !lockedAnswerType && (
						<>
							{!answerType && !submission && (
								<Typography fontSize={{ lg: 22 }}>
									{t('choose')}
								</Typography>
							)}

							<Spacer mb={{ lg: '1.438rem' }} />
							{!answerType && !submission && (
								<FlexLayout>
									<AnswerButton onClick={() => handleChooseType('text')} icon={<Icons.Text />} title={t('text')} />
									<Spacer mx={'2rem'} my={'2rem'}>
										<Typography fontSize={{ lg: 20 }}>Or</Typography>
									</Spacer>
									<AnswerButton onClick={() => handleChooseType('attachment')} icon={<Icons.Attachment />} title={t('attachment')} />
								</FlexLayout>
							)}
						</>
					)}
					{submission && !isEdit && (
						<>
							<Typography fontWeight={300} fontSize={{ lg: '1.375rem' }}>
								{t('you_answer')}
							</Typography>
							<Spacer mb={{ lg: '1.438rem' }} />
						</>
					)}
					{answerType && isEdit && !lockedAnswerType && (
						<FlexLayout onClick={toggleAnswerType} justifyContent={'flex-end'}>
							<SwitchLabel>Switch to {answerType === 'attachment' ? 'Text' : 'Attachment'}</SwitchLabel>
							{answerType === 'text' && (
								<Icon>
									<Icons.Attachment />
								</Icon>
							)}
							{answerType === 'attachment' && (
								<Icon>
									<Icons.Text />
								</Icon>
							)}
						</FlexLayout>
					)}
					<Spacer mb={'0.5rem'} />

					<FlexLayout gridGap={'1rem'} alignItems={'center'}>
						<FlexLayout flex={1}>
							{answerType === 'text' && (
								<Controller
									control={control}
									name="content"
									render={({ field: { value, onChange } }) => {
										// Rich text while editing; the stored answer (always HTML)
										// rendered read-only otherwise. The editor reports what it
										// produced (html / plain); the API normalises both.
										return isEdit ? (
											<EditorBox>
												<RichTextAnswer
													value={value ?? ''}
													onChange={change => {
														onChange(change.value)
														setValue('content_format', change.format, { shouldDirty: true })
													}}
													placeholder="Type something here"
												/>
											</EditorBox>
										) : (
											<ReadOnlyBox>
												<StudentAnswerHtml value={value} />
											</ReadOnlyBox>
										)
									}}
								/>
							)}
							{answerType === 'attachment' && (
								<div>
									<Controller
										control={control}
										name="content"
										render={({ field: { ref, ...props } }) => {
											return <AttachmentAnswer {...props} isEdit={isEdit} />
										}}
									/>
								</div>
							)}
						</FlexLayout>
						{isEdit && (
							<RaisedButton
								onClick={handleSubmitAssignment}
								disabled={!isValid || isSubmitting || !isDirty}
								minWidth={'4.188rem'}
								btnSize={'small'}
								outlined
								btnShape="square"
								withRipple
							>
								Submit
							</RaisedButton>
						)}
					</FlexLayout>
				</div>
			)}
			{!canSubmit && !hasStatus && (
				<div>
					<NoSubmissionCard>
						<Icon color="dark">
							<MdInfoOutline size={'1.2rem'} />
						</Icon>
						<Spacer mx="0.5rem" />
						<Typography fontSize={'1.25rem'} fontWeight={500}>
							{t('disclaimer')}
						</Typography>
					</NoSubmissionCard>
				</div>
			)}

			{hasStatus && submission.feedback && (
				<>
					<Spacer mt="1.875rem" />
					<Typography fontWeight={600} fontSize="1.5rem">
						{t('feedback')}
					</Typography>
					<Spacer mb="1rem" />
					<div dangerouslySetInnerHTML={{ __html: stampAutoDir(submission.feedback) }}></div>
				</>
			)}
		</>
	)
}

export default AssignmentAnswer

const SwitchLabel = styled.p`
	color: #adadad;
	text-decoration: underline;
	font-weight: 500;
	cursor: pointer;
`
const EditorBox = styled.div`
	width: 100%;
`
const ReadOnlyBox = styled.div`
	border-radius: 5px;
	box-shadow: 0 5px 6px 0 rgba(0, 0, 0, 0.16);
	border: solid 1px #c4c4c4;
	width: 100%;
	padding: 1rem 1.625rem;
	font-size: 0.875rem;
	min-height: 8.438rem;
	background: #fafafa;
`
// const NoSubmission = styled.div``

const NoSubmissionCard = styled.div`
	background: #fcecd8;
	padding: 1.125rem;
	border-radius: 4px;
	color: #0d3c61;
	display: flex;
`